<template>
	<b-table striped hover sticky-header="250px" responsive small :items="tblItems" :fields="tblFields" head-variant="dark">
		<template #cell(checkbox)="row">
			<b-form-checkbox class="styleCheckbox" v-if="row.item.checkbox.show" v-model="row.item.checkbox.checked" />
		</template>
		<template #cell(actions)="row">
			<div class="d-flex justify-content-end align-items-center">
				<country-flag
					v-if="row.item.data.isO3166A2"
					class="mr-1 mt-n1"
					:country="row.item.data.isO3166A2"
					rounded="true"
					shadow="true"
				/>
				<k-icon-button
					v-if="row.item.actions.rehomologation"
					:disabled="!userPermissions.update"
					:textTooltip="$t('buttons.rehomologate')"
					@click="$emit('actionRehomologation', row.item.data)"
				>
					<template v-slot:content>
						<b-iconstack font-scale="1">
							<b-icon stacked icon="card-checklist"></b-icon>
							<b-icon stacked icon="exclamation-circle-fill" scale="0.5" shift-h="6" shift-v="-6"></b-icon>
						</b-iconstack>
					</template>
				</k-icon-button>
				<k-icon-button
					v-if="row.item.actions.copyEmail"
					icon="files"
					@click="$emit('actionCopyEmail', row.item.data)"
					:textTooltip="$t('buttons.copy')"
				/>
				<k-icon-button
					v-if="row.item.actions.copy"
					:textTooltip="$t('buttons.copy')"
					icon="files"
					:disabled="!userPermissions.create || !userPermissions.update"
					@click="$emit('actionCopy', row.item.data)"
				/>
				<k-icon-button
					v-if="row.item.actions.edit"
					:textTooltip="$t('buttons.edit')"
					icon="pencil"
					:disabled="!userPermissions.update"
					@click="$emit('actionEdit', row.item.data)"
				/>
				<k-icon-button
					v-if="row.item.actions.validate && !row.item.actions.view"
					icon="file-earmark-check"
					:textTooltip="$t('buttons.validate')"
					:disabled="!userPermissions.update"
					@click="$emit('actionEdit', row.item.data)"
				/>
				<k-icon-button
					v-if="row.item.actions.exclude"
					icon="x"
					:textTooltip="$t('buttons.erase')"
					:disabled="!userPermissions.delete"
					variant="outline-danger"
					@click="$emit('actionExclude', row.item.data)"
				/>
				<k-icon-button
					v-if="row.item.actions.insert"
					icon="plus"
					:textTooltip="$t('buttons.insert')"
					:disabled="!userPermissions.create"
					@click="$emit('actionInsert', row.item.data)"
				/>
				<k-icon-button
					v-if="row.item.actions.view"
					icon="eye"
					:textTooltip="$t('buttons.view')"
					:disabled="!userPermissions.read"
					@click="$emit('actionView', row.item.data)"
					:tooltip="$t('buttons.view')"
				/>
				<k-icon-button
					v-if="row.item.actions.info"
					icon="info-circle"
					:textTooltip="$t('buttons.requirementDetail')"
					:disabled="!userPermissions.read"
					@click="$emit('actionInfo', row.item.data)"
				/>
				<k-icon-button
					v-if="row.item.actions.inactive"
					icon="slash-circle"
					variant="outline-danger"
					:disabled="!userPermissions.delete"
					@click="$emit('actionInactive', row.item.data)"
				/>
				<k-icon-button
					v-if="row.item.actions.active"
					icon="check-square"
					variant="outline-success"
					:disabled="!userPermissions.update"
					@click="$emit('actionActive', row.item.data)"
					:textTooltip="$t('buttons.reactivate')"
				/>
				<k-icon-button
					v-if="row.item.actions.download"
					icon="download"
					@click="$emit('actionDownload', row.item.data)"
					:textTooltip="$t('commons.hovers.downloadFile')"
				/>
				<k-icon-button
					v-if="deleteButton || row.item.actions.delete"
					icon="trash"
					variant="outline-danger"
					:textTooltip="$t('buttons.erase')"
					:disabled="!userPermissions.delete"
					@click="$emit('actionDelete', row.item.data)"
				/>
				<k-icon-button
					v-if="row.item.actions.antecipateValidity && actualUserIsClient"
					icon="file-earmark-x"
					:textTooltip="$t('buttons.revalidate')"
					:disabled="!row.item.data.abbleToAntecipateValidity"
					@click="$emit('actionAntecipateValidity', row.item.data)"
				/>
				<k-icon-button
					v-if="row.item.actions.identityAssume"
					icon="unlock"
					@click="$emit('actionIdentityAssume', row.item.data)"
					:textTooltip="$t('commons.hovers.identityAssume')"
				/>
			</div>
		</template>
		<template v-for="slot in Object.keys($scopedSlots)" :slot="slot" slot-scope="scope">
			<slot :name="slot" v-bind="scope" />
		</template>
	</b-table>
</template>

<script>
import KIconButton from "./Buttons/KIconButton.vue";
import ModalMixin from "@/mixins/ModalMixin";
import CountryFlag from "vue-country-flag";
import { mapGetters } from "vuex";
import portuguese from "@/assets/img/pt-BR.svg";
import english from "@/assets/img/en-US.svg";
import spanish from "@/assets/img/es.svg";

export default {
	components: { KIconButton, CountryFlag },
	mixins: [ModalMixin],
	data() {
		return {
			imgPaths: {
				"pt-BR": portuguese,
				"en-US": english,
				es: spanish
			}
		};
	},
	props: {
		items: {
			type: Array,
			required: true
		},
		fields: {
			type: Array,
			default: () => [],
			required: true
		},
		isSimpleCRUD: {
			type: Boolean,
			default: false
		},
		deleteButton: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		...mapGetters(["userPermissions", "actualUserIsClient"]),
		tblItems: function () {
			let response = [];
			this.items.forEach((item) => {
				let obj = {};

				this.fields.forEach((field) => {
					if (field.getProp) {
						obj[field.key] = field.getProp(item, this.$store.state);
					} else {
						obj[field.key] = item[field.key];
					}
				});

				obj["checkbox"] = item["checkbox"];
				obj["data"] = item;
				obj["actions"] = {
					copy: false,
					expand: false,
					edit: false,
					exclude: false,
					insert: false,
					inactive: false,
					view: false,
					...item["actions"]
				};
				response.push(obj);
			});

			return response;
		},
		tblFields: function () {
			const fields = [...this.fields, { key: "actions", label: "", class: "text-right text-nowrap" }];
			if (this.items.some((f) => f.checkbox?.show)) {
				fields.unshift({ key: "checkbox", label: "" });
			}
			return fields;
		}
	},
	methods: {
		getLanguageImgPath(languageId) {
			return this.imgPaths[languageId];
		}
	}
};
</script>

<style>
.styleCheckbox {
	display: flex;
	justify-content: center;
	margin: 0;
	padding-top: 0;
	padding-right: 0;
}
div.styleCheckbox label::before {
	left: -1rem;
}
div.styleCheckbox label::after {
	left: -1rem;
}
.fullHeight {
	max-height: calc(100% - 250px);
}
.b-table-sticky-header > .table.b-table > thead > tr > th {
	position: sticky !important;
}
.country-flag {
	font-size: 12px;
}
</style>
